
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Camera, Upload } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { uploadProfilePhoto, updateUserProfile } from "@/services/userProfileService";
import { supabase } from "@/integrations/supabase/client";
import { Camera as CapacitorCamera, CameraResultType, CameraSource } from '@capacitor/camera';

interface ProfilePhotoUploaderProps {
  userId: string;
  initialUrl?: string;
  editable?: boolean;
  initials?: string;
  onPhotoUpdated?: (newUrl: string) => void;
  featured?: boolean;
}

export const ProfilePhotoUploader: React.FC<ProfilePhotoUploaderProps> = ({
  userId,
  initialUrl,
  editable = false,
  initials = "?",
  onPhotoUpdated,
  featured = false
}) => {
  const { t } = useTranslation(["coach", "common"]);
  const [previewPhotoUrl, setPreviewPhotoUrl] = useState<string | null>(initialUrl || null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check if we're on a mobile platform
  const isMobile = window.Capacitor?.isNativePlatform() || false;

  const triggerFileInput = () => fileInputRef.current?.click();

  useEffect(() => {
    if (initialUrl) {
      setPreviewPhotoUrl(initialUrl);
    }
  }, [initialUrl]);

  const convertDataUrlToFile = (dataUrl: string, filename: string): File => {
    const arr = dataUrl.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  const handleCapacitorCamera = async () => {
    try {
      const image = await CapacitorCamera.getPhoto({
        quality: 80,
        allowEditing: true,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Prompt,
        width: 300,
        height: 300
      });

      if (image.dataUrl) {
        const file = convertDataUrlToFile(image.dataUrl, `profile_${Date.now()}.jpeg`);
        await uploadPhoto(file);
      }
    } catch (error) {
      console.error('Camera error:', error);
      if (error instanceof Error && error.message !== 'User cancelled photos app') {
        toast.error(t("photoUploadError", { ns: "coach" }));
      }
    }
  };

  const uploadPhoto = async (file: File) => {
    const previewURL = URL.createObjectURL(file);
    setPreviewPhotoUrl(previewURL);
    setIsUploading(true);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        toast.error(t("authRequired", { ns: "coach" }));
        URL.revokeObjectURL(previewURL);
        setPreviewPhotoUrl(initialUrl || null);
        return;
      }
      
      console.log('Uploading photo for user ID:', sessionData.session.user.id);
      const photoUrl = await uploadProfilePhoto(sessionData.session.user.id, file);

      if (photoUrl) {
        URL.revokeObjectURL(previewURL);
        await updateUserProfile(userId, { profile_photo_url: photoUrl });
        setPreviewPhotoUrl(photoUrl);
        toast.success(t("photoUpdated", { ns: "coach" }));
        onPhotoUpdated?.(photoUrl);
      } else {
        throw new Error("Failed to get uploaded photo URL");
      }
    } catch (error) {
      console.error("Photo upload error:", error);
      toast.error(t("photoUploadError", { ns: "coach" }));
      setPreviewPhotoUrl(initialUrl || null);
    } finally {
      setIsUploading(false);
    }
  };
  
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;
    const file = event.target.files[0];
    await uploadPhoto(file);
  };

  const handlePhotoAction = () => {
    triggerFileInput();
    // if (isMobile) {
    //   handleCapacitorCamera();
    // } else {
    //   triggerFileInput();
    // }
  };

  return (
    <div className={featured ? "flex flex-col items-center gap-3" : "flex flex-col sm:flex-row items-center gap-4"}>
      <div className="relative">
        <Avatar className={featured ? "h-24 w-24 border-2 border-primary shadow-[0_0_24px_hsl(var(--primary)/0.28)] md:h-28 md:w-28" : "h-24 w-24"}>
          {previewPhotoUrl ? (
            <AvatarImage src={previewPhotoUrl} alt="Profile" />
          ) : (
            <AvatarFallback>{initials}</AvatarFallback>
          )}
        </Avatar>
        {editable && (
          <Button
            variant="secondary"
            size="icon"
            className="absolute bottom-0 right-0 rounded-full border border-primary bg-primary text-primary-foreground hover:bg-primary/90"
            onClick={handlePhotoAction}
            disabled={isUploading}
            aria-label={t("changePhotoIcon", { ns: "coach" })}
          >
            <Camera className="h-4 w-4" />
          </Button>        
        )}
      </div>
      <div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileUpload}
          disabled={!editable || isUploading}
        />
        {editable && !featured && (
          <Button
            variant="outline"
            onClick={handlePhotoAction}
            disabled={isUploading}
            className="mb-2"
          >
            {isMobile ? (
              <Camera className="h-4 w-4 mr-2" />
            ) : (
              <Upload className="h-4 w-4 mr-2" />
            )}
            {isUploading ? t("uploading", { ns: "common" }) : t("changePhoto", { ns: "coach" })}
          </Button>
        )}
        {editable && featured && (
          <Button
            variant="outline"
            size="sm"
            onClick={handlePhotoAction}
            disabled={isUploading}
            className="border-primary/70 bg-background/75 text-primary hover:bg-primary/10 hover:text-primary"
          >
            <Camera className="h-4 w-4" />
            {isUploading ? t("uploading", { ns: "common" }) : t("changePhoto", { ns: "coach" })}
          </Button>
        )}
        {previewPhotoUrl && !featured && (
          <p className="text-sm text-muted-foreground">
            {t("currentPhoto", { ns: "coach" })}
          </p>
        )}
      </div>
    </div>
  );
};
